import { useRouterState } from "@tanstack/react-router";
import { localeFromPath, type Locale } from "./locale";
import { en, type UiKey } from "./dictionaries/en";
import { es } from "./dictionaries/es";
import { counterpartPath, localizedPath } from "./routes";

const DICTS: Record<Locale, Record<string, string>> = { en, es };

/** Translate a UI key for an explicit locale (safe on the server). */
export function t(locale: Locale, key: UiKey): string {
  return DICTS[locale][key] ?? en[key];
}

/** Locale of the page currently rendering, derived from the URL. */
export function useLocale(): Locale {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return localeFromPath(pathname);
}

/** `const tr = useT()` → `tr("nav.startMyApproval")`. */
export function useT() {
  const locale = useLocale();
  return (key: UiKey) => t(locale, key);
}

/** Everything a shared component needs: locale, translator and href helper. */
export function useI18n() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = localeFromPath(pathname);
  return {
    locale,
    pathname,
    isSpanish: locale === "es",
    t: (key: UiKey) => t(locale, key),
    /** Map an English route path to the current locale. */
    href: (enPath: string) => localizedPath(enPath, locale),
    /** The same page in the other language, or null when untranslated. */
    other: counterpartPath(pathname, locale === "es" ? "en" : "es"),
  };
}
